import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { CountryCode } from '@delmonicos/shared-design-system-react/src/components/country-code/country-code';
import { Divider } from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import { TextInput } from '@delmonicos/shared-design-system-react/src/components/input/text-input';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { useGatewayUpdateCustomerFormValidation } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/customers/models/update-customer.form-validation';
import { GatewayUpdateCustomerModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/customers/models/update-customer.model';
import { UpdateCustomerModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/customers-service-lib/src/wrapper/inputs/customers/update-customer.model';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export const CustomerUpdateFormStep1InformationFragment =
  (): React.JSX.Element => {
    const { t } = useTranslation();
    const {
      customerName,
      customerHolderCompanyName,
      customerHolderSiret,
      customerHolderVatNumber,
      customerHolderAddressAddress,
      customerHolderAddressZipCode,
      customerHolderAddressCity,
      customerHolderAddressCountryCode,
    } = useGatewayUpdateCustomerFormValidation();
    const { control } = useFormContext<
      Pick<UpdateCustomerModel, 'customerId'> & {
        customer: GatewayUpdateCustomerModel;
      }
    >();

    return (
      <Card gap={GapEnum.L} direction="column" flex={1}>
        <AccentTitle
          icon="info-circle"
          text={t('$.customer-details.information.title')}
        />
        <Controller
          name="customer.name"
          rules={customerName}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-customer.name')}
              placeholder={t('delmonicos-shared-sdk.outputs-customer.name')}
              required={!!customerName.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e.target.value === '' ? '' : e.target.value);
              }}
            />
          )}
        />
        <Divider />

        <Controller
          name="customer.holder.companyName"
          rules={customerHolderCompanyName}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-holder.company-name')}
              placeholder={t(
                'delmonicos-shared-sdk.outputs-holder.company-name',
              )}
              required={!!customerHolderCompanyName.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e.target.value === '' ? '' : e.target.value);
              }}
            />
          )}
        />
        <Controller
          name="customer.holder.siret"
          rules={customerHolderSiret}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-holder.siret')}
              placeholder={t('delmonicos-shared-sdk.outputs-holder.siret')}
              required={!!customerHolderSiret.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e.target.value === '' ? '' : e.target.value);
              }}
            />
          )}
        />
        <Controller
          name="customer.holder.vatNumber"
          rules={customerHolderVatNumber}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-holder.vat-number')}
              placeholder={t('delmonicos-shared-sdk.outputs-holder.vat-number')}
              required={!!customerHolderVatNumber.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e.target.value === '' ? '' : e.target.value);
              }}
            />
          )}
        />
        <Divider />
        <Controller
          name="customer.holder.address.address"
          rules={customerHolderAddressAddress}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-address.address')}
              placeholder={t('delmonicos-shared-sdk.outputs-address.address')}
              required={!!customerHolderAddressAddress.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e.target.value === '' ? '' : e.target.value);
              }}
            />
          )}
        />
        <Stack gap={GapEnum.S} justifyContent="space-between" flexWrap="wrap">
          <Controller
            name="customer.holder.address.zipCode"
            rules={customerHolderAddressZipCode}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                label={t('delmonicos-shared-sdk.outputs-address.zip-code')}
                placeholder={t(
                  'delmonicos-shared-sdk.outputs-address.zip-code',
                )}
                required={!!customerHolderAddressZipCode.required}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e.target.value === '' ? '' : e.target.value);
                }}
              />
            )}
          />
          <Controller
            name="customer.holder.address.city"
            rules={customerHolderAddressCity}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                label={t('delmonicos-shared-sdk.outputs-address.city')}
                placeholder={t('delmonicos-shared-sdk.outputs-address.city')}
                required={!!customerHolderAddressCity.required}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(e.target.value === '' ? '' : e.target.value);
                }}
              />
            )}
          />
          <Controller
            name="customer.holder.address.countryCode"
            control={control}
            rules={customerHolderAddressCountryCode}
            render={({ field, fieldState: { error } }) => (
              <CountryCode
                errorMessage={error?.message}
                fieldValue={field.value}
                fieldOnChange={field.onChange}
              />
            )}
          />
        </Stack>
      </Card>
    );
  };

export const CustomerUpdateFormStep1HolderFragment = (): React.JSX.Element => {
  const { t } = useTranslation();
  const {
    customerHolderFirstName,
    customerHolderLastName,
    customerHolderEmail,
  } = useGatewayUpdateCustomerFormValidation();
  const { control } = useFormContext<
    Pick<UpdateCustomerModel, 'customerId'> & {
      customer: GatewayUpdateCustomerModel;
    }
  >();

  return (
    <Card gap={GapEnum.M} flex={1}>
      <AccentTitle
        icon="user-admin"
        variant={AccentVariantEnum.Secondary}
        text={t('delmonicos-shared-sdk.outputs-customer.holder')}
      />

      <Stack gap={GapEnum.M} flexWrap="wrap">
        <Controller
          name="customer.holder.firstName"
          rules={customerHolderFirstName}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-holder.first-name')}
              placeholder={t('delmonicos-shared-sdk.outputs-holder.first-name')}
              required={!!customerHolderFirstName.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e.target.value === '' ? '' : e.target.value);
              }}
            />
          )}
        />
        <Controller
          name="customer.holder.lastName"
          rules={customerHolderLastName}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextInput
              label={t('delmonicos-shared-sdk.outputs-holder.last-name')}
              placeholder={t('delmonicos-shared-sdk.outputs-holder.last-name')}
              required={!!customerHolderLastName.required}
              errorMessage={error?.message}
              {...field}
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e.target.value === '' ? '' : e.target.value);
              }}
            />
          )}
        />
      </Stack>

      <Controller
        name="customer.holder.email"
        rules={customerHolderEmail}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextInput
            label={t('delmonicos-shared-sdk.outputs-holder.email')}
            placeholder={t('delmonicos-shared-sdk.outputs-holder.email')}
            required={!!customerHolderEmail.required}
            errorMessage={error?.message}
            {...field}
            value={field.value ?? ''}
            onChange={(e) => {
              field.onChange(e.target.value === '' ? '' : e.target.value);
            }}
          />
        )}
      />
    </Card>
  );
};

export const CustomerUpdateFormStep1Controller = (): React.JSX.Element => {
  return (
    <Stack gap={GapEnum.L} flex={1}>
      <Stack flex={1}>
        <CustomerUpdateFormStep1InformationFragment />
      </Stack>

      <Stack flex={1}>
        <CustomerUpdateFormStep1HolderFragment />
      </Stack>
    </Stack>
  );
};
